import fetch from 'unfetch';
import React from 'react';
import { ApolloClient, ApolloLink, Operation, ServerError } from '@apollo/client';
import { InMemoryCache } from '@apollo/client/cache';
import { onError } from '@apollo/client/link/error';
import { createHttpLink } from '@apollo/client/link/http';
import { RetryLink } from '@apollo/client/link/retry';
import Cookies from 'js-cookie';
import { FormatSpecialCharactersMiddleware } from 'shared-components/utils';
import { gql } from '@apollo/client';
import { determineError } from 'shared-components/models/ApolloHandlerModel';
import { Logger } from 'shared-components/utils';
import { ApolloProvider } from '@apollo/client';
import { useMemo } from 'react';
import ErrorModal from 'op-components/ErrorModal/ErrorModal';
import { ERROR_TYPE, useErrorModalContext } from 'op-contexts/ErrorModalContext/ErrorModalContext';
import { NetworkError } from '@apollo/client/errors';
import { GraphQLFormattedError } from 'graphql';
import * as Sentry from '@sentry/browser';

// Replace numeric and UUID path segments to avoid leaking patient IDs in Sentry
const sanitisePath = (path: string): string =>
  path.replace(/\/[0-9a-f-]{8,}(\/|$)/gi, '/[id]$1').replace(/\/\d+(\/|$)/g, '/[id]$1');

const logger = new Logger('ApolloHandler');

// LUM-2005: HTTP 401 (new backend behaviour) and legacy 403 both mean the session
// has expired; both must redirect to login while the backend change rolls out.
export const SESSION_EXPIRY_STATUS_CODES: readonly number[] = [401, 403];

export const LOGIN_PATH = '/sso/login';

const redirectToLogin = (source: string, statusCode: number, extra: Record<string, unknown>): void => {
  Sentry.captureMessage(`Apollo ${statusCode} redirect (${source})`, {
    level: 'warning',
    extra: { statusCode, ...extra },
  });
  window.location.replace(LOGIN_PATH);
};

const safeStringify = (obj: any): string => {
  // Helper function to safely stringify objects within errors or headers
  try {
    if (!obj) return 'null';
    if (typeof obj === 'string') return obj;
    if (typeof obj === 'number' || typeof obj === 'boolean') return String(obj);

    // Handle Error objects specially
    if (obj instanceof Error) {
      const errorObj: Record<string, any> = {
        name: obj.name,
        message: obj.message,
        stack: obj.stack,
      };

      // Add any additional properties that might be on the error
      Object.getOwnPropertyNames(obj).forEach((key) => {
        if (key !== 'name' && key !== 'message' && key !== 'stack') {
          errorObj[key] = (obj as any)[key];
        }
      });

      return JSON.stringify(errorObj, null, 2);
    }

    // For other objects, try to stringify their enumerable properties
    return JSON.stringify(
      obj,
      (key, value) => {
        if (value === undefined) return 'undefined';
        if (value === null) return 'null';
        if (typeof value === 'function') return '[Function]';
        return value;
      },
      2,
    );
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return `Error stringifying object: ${errorMessage}`;
  }
};

const logToSentry = (
  operation: Operation,
  graphQLErrors: readonly GraphQLFormattedError[] | undefined,
  networkError: NetworkError | undefined,
) => {
  // Creates an object from the operation and error and logs it to Sentry
  const requestInfo = {
    operationName: operation?.operationName ?? 'Unknown',
    body: operation?.query?.loc?.source?.body ?? 'Unknown',
    graphQLErrors: graphQLErrors ? safeStringify(graphQLErrors) : 'No GraphQL errors',
    networkErrors: networkError ? safeStringify(networkError) : 'No network errors',
    url: operation.getContext()?.response?.url ?? 'Unknown URL',
    timestamp: new Date().toISOString(),
  };

  // If it's a network error, just provide that as the title.
  // We should be fine to group these together as they shouldn't be indicating a problem with the specific operation
  // But for graphql errors, we want to use the Operation name because many possible
  // errors can include data that makes sentry unable to group them
  // There can also be more than one error
  const alertTitle =
    'Apollo: ' + networkError
      ? `Network error: ${networkError?.message || networkError}`
      : `Operation ${operation?.operationName} Failed`;

  Sentry.captureMessage(alertTitle, {
    level: 'error',
    extra: requestInfo,
  });
};

export const createErrorLink = (setError: (errorType: ERROR_TYPE) => void): ApolloLink => {
  /*
    Global error handler for all Apollo Graphql requests.
  */
  return onError(({ graphQLErrors, networkError, operation }): void => {
    const context = operation.getContext();
    const locallyHandledErrors: string[] = context.locallyHandledErrors || [];

    // NOTE: This handling of network errors is now done by the retryLink so the following block is likely not needed anymore
    if (networkError) {
      const networkStatusCode: number = (networkError as ServerError)?.statusCode ?? -1;
      if (SESSION_EXPIRY_STATUS_CODES.includes(networkStatusCode)) {
        redirectToLogin('network', networkStatusCode, { operationName: operation?.operationName });
        return;
      }

      // Catch any random network errors, this includes full network failures
      // TODO type is not a valid property of networkError, so this might not actually ever trigger
      // @ts-ignore
      if (networkError.type == 'error' && networkStatusCode === -1) {
        // @ts-ignore
        setError('Generic');
      }
      return;
    }

    // Handle all other graphQLErrors
    if (graphQLErrors && graphQLErrors.length) {
      // Certain session expiry is returned by the backend as a graphQLError with a stringified JSON object in the message like this
      // LOGGED_IN_ERROR = json.dumps(dict(message="You must be logged in", statusCode=403))
      // If it exists in any of the graphQLErrors, redirect to the login page
      // TODO: handle these with just a message or returning a network error
      for (const error of graphQLErrors) {
        const formattedError = determineError(error);
        if (formattedError && SESSION_EXPIRY_STATUS_CODES.includes(formattedError.statusCode)) {
          redirectToLogin('graphQL', formattedError.statusCode, { operationName: operation?.operationName });
          return;
        }
      }

      // Handle all other graphQLErrors
      graphQLErrors.forEach((error) => {
        if (locallyHandledErrors.includes(error.message)) {
          // This has been set at the useQuery or useMutation so we don't need to handle it here
          return;
        }

        // Trigger the error popup
        setError('Generic');

        // Send the error to Sentry
        logToSentry(operation, graphQLErrors, networkError);
      });
      // We had errors and we've handled them, so return
    } else {
      // For some reason this function fired but there were no errors to handle
      // This should never happpen, but we want a fallback just in case it does for now
      logToSentry(operation, graphQLErrors, networkError);
    }
  });
};

const httpLink = createHttpLink({
  uri: '/server/graphql',
  fetch,
  credentials: 'same-origin',
});

const csrfMiddleware = new ApolloLink((operation: any, forward) => {
  operation.setContext(({ headers = {} }: { headers: any }): any => ({
    headers: {
      ...headers,
      'X-CSRFTOKEN': Cookies.get('csrftoken'),
    },
  }));

  return forward(operation);
});

const EXTEND_LOCK = 'ExtendLock';
const OPERATIONS_TO_IGNORE = [EXTEND_LOCK];

let sessionTimeout: NodeJS.Timeout;
// Handle redirecting the user when their session has expired
export const sessionTimeoutLink = new ApolloLink((operation: any, forward: any) => {
  return forward(operation).map((response: any) => {
    const context = operation.getContext();
    // Defensive: context.response may be undefined (e.g. error responses, custom fetch)
    const httpResponse = context?.response;
    const headers = httpResponse?.headers;
    if (!headers) return response;

    const expiry = headers.get('X-Expiry-Seconds');

    // currently timeout set for 15 mins for patients, 24h for staff
    const userRedirectMapping = {
      staff: '/',
      patientInClinic: '/patient',
      patientOpHomeRego: '/timeout',
      patientPortal: '/timeout',
      patient: '/timeout',
    };
    const userType = headers.get('X-User-Type');
    let redirectTo = userRedirectMapping[userType as keyof typeof userRedirectMapping];

    // Defensive: context.cache.data.data may be undefined (e.g. on first request, or error)
    const cacheData = context?.cache?.data?.data;
    const data = cacheData ? JSON.parse(JSON.stringify(cacheData)) : {};

    // Iterate over the GraphQL cache to try to tease out the logged in User object
    const getUserProperty = Object.keys(data)
      .map((property: any) =>
        data.hasOwnProperty(property) && property.toString().startsWith('UserType') ? data[property] : null,
      )
      .filter(Boolean);

    const props = ['isRo', 'isPso', 'isSuperuser'];

    // Change redirection path if a User has one of the above properties and one of those properties is true
    redirectTo =
      getUserProperty.length &&
      props.some((prop) => getUserProperty[0].hasOwnProperty(prop) && getUserProperty[0][prop])
        ? '/'
        : redirectTo;

    if (!expiry) return response;

    if (expiry && !OPERATIONS_TO_IGNORE.includes(operation?.operationName)) {
      if (sessionTimeout) clearTimeout(sessionTimeout);
      sessionTimeout = setTimeout(() => {
        if (document.location.pathname !== redirectTo) {
          // Timing out an idle session is designed behaviour, not a fault — a
          // breadcrumb keeps the redirect visible on any later real error
          // without opening a Sentry issue for every idle patient (AU-HORIZON-PROD-6E6).
          Sentry.addBreadcrumb({
            category: 'session',
            message: 'Session timeout redirect',
            level: 'info',
            data: {
              operationName: operation?.operationName,
              expiry,
              userType,
              redirectTo,
              currentPath: sanitisePath(document.location.pathname),
            },
          });
          document.location.href = redirectTo;
          document.cookie = 'SESSION_COOKIE_AGE=; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
        }
      }, 1000 * expiry); // Expiry is in seconds, timeout takes ms
    }
    return response;
  });
});

export const createRetryLink = (setError: (errorType: ERROR_TYPE) => void): RetryLink => {
  return new RetryLink({
    attempts: (count, operation, error) => {
      // Session expiry (401/403) must redirect immediately, never retry
      const statusCode: number = (error as ServerError)?.statusCode ?? -1;
      if (SESSION_EXPIRY_STATUS_CODES.includes(statusCode)) {
        redirectToLogin('retry', statusCode, { operationName: operation?.operationName, retryCount: count });
        return false;
      }

      if (count > 4) {
        logToSentry(operation, [], error);
        return false;
      }

      // Return true to retry if we're still erroring and under the count
      window.console.log('retry', count, operation.operationName);
      return Boolean(error);
    },

    delay: {
      initial: 300,
      max: Infinity,
      jitter: true,
    },
  });
};

const createApolloClient = (setError: (errorType: ERROR_TYPE) => void) => {
  const errorLink = createErrorLink(setError);
  const retryLink = createRetryLink(setError);
  const link = ApolloLink.from([
    retryLink,
    FormatSpecialCharactersMiddleware,
    errorLink,
    sessionTimeoutLink,
    csrfMiddleware,
    httpLink,
  ]);

  const cache = new InMemoryCache();

  // Apollo client
  const apolloClient = new ApolloClient({
    link,
    cache: cache,
    connectToDevTools: true,
  });

  cache.writeQuery({
    query: gql`
      query {
        pendingSaveCount
        saveErrorCount
        registrationPagesViewed
        error {
          message
          statusCode
        }
      }
    `,
    data: {
      pendingSaveCount: 0,
      saveErrorCount: 0,
      registrationPagesViewed: [],
      error: {
        __typename: 'Error',
        message: '',
        statusCode: -1,
      },
    },
  });
  return apolloClient;
};

export const ApolloProviderWithError = ({ children }: { children: React.ReactNode }) => {
  const { error, setError } = useErrorModalContext();
  const client = useMemo(() => createApolloClient(setError), [setError]);
  return (
    <ApolloProvider client={client}>
      {children}
      <ErrorModal key={error ? 'error-modal' : 'no-error'} />
    </ApolloProvider>
  );
};
